import { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { X } from 'lucide-react';
import type { OrderState } from '../../pages/CreateSalesOrderPage';

interface Props {
  state: OrderState;
  updateState: (updater: (prev: OrderState) => OrderState) => void;
  onClose: () => void;
}

export function GSTConfigModal({ state, updateState, onClose }: Props) {
  const [config, setConfig] = useState(state.gstConfig);

  const handleSave = () => {
    updateState(prev => {
      const newState = { ...prev, gstConfig: config };
      // Recalculate totals if taxCalculation changed
      if (prev.gstConfig.taxCalculation !== config.taxCalculation) {
        const newItems = prev.items.map(item => {
          const total = item.qty * item.rate;
          const inclusive = config.taxCalculation === 'inclusive';
          const amount = inclusive ? total / (1 + (item.gstRate / 100)) : total;
          const taxAmount = inclusive ? total - amount : amount * (item.gstRate / 100);
          
          return {
            ...item,
            amount: Number(amount.toFixed(2)),
            cgst: Number((taxAmount / 2).toFixed(2)),
            sgst: Number((taxAmount / 2).toFixed(2)),
            total: Number((amount + taxAmount).toFixed(2))
          };
        });
        newState.items = newItems;
      }
      return newState;
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-semibold text-lg text-gray-900">Configure GST Settings</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tax Treatment</label>
            <select
              className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary p-2"
              value={config.taxTreatment}
              onChange={(e) => setConfig({ ...config, taxTreatment: e.target.value })}
            >
              <option value="registered">Registered Business (Regular)</option>
              <option value="unregistered">Unregistered Business</option>
              <option value="sez">SEZ (Special Economic Zone)</option>
              <option value="overseas">Overseas (Export)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Tax Preference</label>
            <select
              className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary p-2"
              value={config.taxPreference}
              onChange={(e) => setConfig({ ...config, taxPreference: e.target.value })}
            >
              <option value="taxable">Taxable</option>
              <option value="non_taxable">Non-Taxable</option>
              <option value="out_of_scope">Out of Scope</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tax Calculation</label>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="taxCalc"
                  value="exclusive"
                  checked={config.taxCalculation === 'exclusive'}
                  onChange={(e) => setConfig({ ...config, taxCalculation: e.target.value as 'exclusive' | 'inclusive' })}
                  className="text-primary focus:ring-primary h-4 w-4"
                />
                <span className="text-sm text-gray-700">Tax Exclusive</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="taxCalc"
                  value="inclusive"
                  checked={config.taxCalculation === 'inclusive'}
                  onChange={(e) => setConfig({ ...config, taxCalculation: e.target.value as 'exclusive' | 'inclusive' })}
                  className="text-primary focus:ring-primary h-4 w-4"
                />
                <span className="text-sm text-gray-700">Tax Inclusive</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">If inclusive, item rate already includes GST.</p>
          </div>
        </div>
        
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save Configuration</Button>
        </div>
      </div>
    </div>
  );
}

export function FormatConfigModal({ state, updateState, onClose }: Props) {
  const [config, setConfig] = useState(state.numberFormat);

  const handleSave = () => {
    updateState(prev => ({ ...prev, numberFormat: config }));
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-semibold text-lg text-gray-900">Number & Currency Format</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Number Locale Format</label>
            <select
              className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary p-2"
              value={config.locale}
              onChange={(e) => setConfig({ ...config, locale: e.target.value })}
            >
              <option value="en-IN">Indian (1,23,456.00)</option>
              <option value="en-US">US/International (123,456.00)</option>
              <option value="en-GB">British (123,456.00)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Decimal Places</label>
            <select
              className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary p-2"
              value={config.currencyDecimals}
              onChange={(e) => setConfig({ ...config, currencyDecimals: Number(e.target.value) })}
            >
              <option value={0}>0 (None)</option>
              <option value={2}>2 (0.00)</option>
              <option value={3}>3 (0.000)</option>
              <option value={4}>4 (0.0000)</option>
            </select>
          </div>
        </div>
        
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save Format</Button>
        </div>
      </div>
    </div>
  );
}

export function TableColumnsModal({ state, updateState, onClose }: Props) {
  const [config, setConfig] = useState(state.tableConfig);

  const handleSave = () => {
    updateState(prev => ({ ...prev, tableConfig: config }));
    onClose();
  };

  const toggle = (key: keyof typeof config) => {
    setConfig(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-semibold text-lg text-gray-900">Edit Table Columns</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-500 mb-2">Select which columns to show in the line items grid.</p>
          
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={config.showHsn} onChange={() => toggle('showHsn')} className="text-primary rounded focus:ring-primary h-4 w-4" />
            <span className="text-sm text-gray-700">HSN/SAC</span>
          </label>
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={config.showGstRate} onChange={() => toggle('showGstRate')} className="text-primary rounded focus:ring-primary h-4 w-4" />
            <span className="text-sm text-gray-700">GST Rate (%)</span>
          </label>
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={config.showAmount} onChange={() => toggle('showAmount')} className="text-primary rounded focus:ring-primary h-4 w-4" />
            <span className="text-sm text-gray-700">Amount (Before Tax)</span>
          </label>
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={config.showCgst} onChange={() => toggle('showCgst')} className="text-primary rounded focus:ring-primary h-4 w-4" />
            <span className="text-sm text-gray-700">CGST Amount</span>
          </label>
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={config.showSgst} onChange={() => toggle('showSgst')} className="text-primary rounded focus:ring-primary h-4 w-4" />
            <span className="text-sm text-gray-700">SGST Amount</span>
          </label>
        </div>
        
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save Columns</Button>
        </div>
      </div>
    </div>
  );
}
